import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { BlogCta } from '../components/BlogCta';
import { Seo } from '../components/Seo';
import { track } from '../lib/analytics';
import { formatPostDate, getAllPosts, getPostBySlug } from '../lib/blog';

const SITE_URL = 'https://prepzio.io';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : null;

  useEffect(() => {
    if (!slug) return;
    const current = getPostBySlug(slug);
    if (!current) return;
    track('blog_post_view', { slug: current.slug, title: current.title });
  }, [slug]);

  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const path = `/blog/${post.slug}`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: {
      '@type': 'Organization',
      name: 'Prepzio',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Prepzio',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${path}`,
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${SITE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `${SITE_URL}${path}`,
      },
    ],
  };

  const otherPosts = getAllPosts()
    .filter((p) => p.slug !== post.slug)
    .slice(0, 1);

  return (
    <>
      <Seo
        title={`${post.title} · Prepzio Blog`}
        description={post.description}
        path={path}
        ogType="article"
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <article className="section blog-post">
        <div className="container-narrow">
          <nav className="blog-post__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/blog">Blog</Link>
          </nav>

          <header className="blog-post__header">
            <p className="eyebrow">Prepzio Blog</p>
            <h1>{post.title}</h1>
            <div className="blog-post__meta">
              <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
              <span aria-hidden="true">·</span>
              <span>{post.readingTimeMinutes} min read</span>
            </div>
            {post.tags.length > 0 && (
              <ul className="blog-post__tags" aria-label="Tags">
                {post.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            )}
          </header>

          <div className="blog-prose" dangerouslySetInnerHTML={{ __html: post.html }} />

          {otherPosts.length > 0 && (
            <section className="blog-related" aria-labelledby="related-title">
              <h2 id="related-title">Related reading</h2>
              <ul>
                {otherPosts.map((related) => (
                  <li key={related.slug}>
                    <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <BlogCta />
        </div>
      </article>
    </>
  );
};

export default BlogPost;

export function getStaticPaths() {
  return getAllPosts().map((post) => `blog/${post.slug}`);
}
